(function() { window.GUID_a20d7b26_7d11_457b_af76_fc316668ccff = { getGuid: function() { return "a20d7b26-7d11-457b-af76-fc316668ccff"; } }; })();
/*
Example usage

Basic key-value:
  const kv = CS.IDB.createKV({ dbName: "my-app", storeName: "keyval" });
  await kv.set("theme", "dark");
  const theme = await kv.get<string>("theme"); // "dark"
  await kv.del("theme");

Namespacing:
  const root = CS.IDB.createKV({ dbName: "my-app" });
  const user123 = root.withNamespace("user:123");
  const user999 = root.withNamespace("user:999");
  await user123.set("token", "abc");
  await user999.set("token", "zzz");
  console.log(await user123.get("token")); // "abc"
  console.log(await user999.get("token")); // "zzz"

TTL / expiry:
  const cache = CS.IDB.createKV({ dbName: "my-app", namespace: "cache" });
  await cache.set("profile", { name: "Ada" }, { ttl: 60_000 }); // 1 minute
  const profile = await cache.get<{ name: string }>("profile");
  // After 1 min, get() returns undefined and lazily removes it.

Proactive cleanup of expired records:
  await cache.cleanupExpired();      // this namespace only
  await cache.cleanupExpired("all"); // all namespaces
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var CS;
(function (CS) {
    var IDB;
    (function (IDB) {
        /* idb-kv.ts
         A tiny, robust IndexedDB key-value store with namespacing + optional TTL.
      
         - One DB, one object store
         - Primary key: compound keyPath ["ns", "k"]
         - Index: byNs (namespace)
         - Index: byExpiry (expiresAt)
      */
        const DEFAULT_DB = "idb-kv";
        const DEFAULT_STORE = "kv";
        const INDEX_BY_NS = "byNs";
        const INDEX_BY_EXPIRY = "byExpiry";
        const dbPromiseCache = new Map();
        function ensureIDB() {
            if (typeof indexedDB === "undefined") {
                throw new Error("IndexedDB is not available in this environment.");
            }
            return indexedDB;
        }
        function cacheKey(dbName, storeName) {
            return `${dbName}::${storeName}`;
        }
        function promisifyRequest(req) {
            return new Promise((resolve, reject) => {
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            });
        }
        function txDone(tx) {
            return new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve();
                tx.onabort = () => {
                    var _a;
                    return reject((_a = tx.error) !== null && _a !== void 0 ? _a : new DOMException("Transaction aborted", "AbortError"));
                };
                tx.onerror = () => {
                    var _a;
                    return reject((_a = tx.error) !== null && _a !== void 0 ? _a : new DOMException("Transaction failed", "UnknownError"));
                };
            });
        }
        function normalizeExpiresAt(options) {
            if (!options)
                return undefined;
            if (options.expiresAt === null)
                return undefined;
            if (options.expiresAt instanceof Date) {
                const t = options.expiresAt.getTime();
                return Number.isFinite(t) ? t : undefined;
            }
            if (typeof options.expiresAt === "number") {
                return Number.isFinite(options.expiresAt) ? options.expiresAt : undefined;
            }
            if (typeof options.ttl === "number") {
                if (!Number.isFinite(options.ttl))
                    return undefined;
                return Date.now() + options.ttl;
            }
            return undefined;
        }
        function isExpired(e) {
            return typeof e === "number" && e <= Date.now();
        }
        function openDatabase(dbName, storeName) {
            return __awaiter(this, void 0, void 0, function* () {
                ensureIDB();
                const key = cacheKey(dbName, storeName);
                const cached = dbPromiseCache.get(key);
                if (cached)
                    return cached;
                const p = new Promise((resolve, reject) => {
                    let req;
                    try {
                        req = indexedDB.open(dbName, 1);
                    }
                    catch (err) {
                        reject(err);
                        return;
                    }
                    req.onupgradeneeded = () => {
                        const db = req.result;
                        if (!db.objectStoreNames.contains(storeName)) {
                            const store = db.createObjectStore(storeName, {
                                keyPath: ["ns", "k"],
                            });
                            store.createIndex(INDEX_BY_NS, "ns", { unique: false });
                            store.createIndex(INDEX_BY_EXPIRY, "e", { unique: false });
                            return;
                        }
                        // Store exists: validate schema and ensure indexes exist.
                        const tx = req.transaction;
                        if (!tx)
                            throw new Error("Missing upgrade transaction.");
                        const store = tx.objectStore(storeName);
                        const kp = store.keyPath;
                        const kpOk = Array.isArray(kp) &&
                            kp.length === 2 &&
                            kp[0] === "ns" &&
                            kp[1] === "k";
                        if (!kpOk) {
                            // Cannot change keyPath of existing store. Fail loudly with guidance.
                            throw new Error(`IndexedDB store "${storeName}" in DB "${dbName}" exists with an incompatible schema.\n` +
                                `Choose a different dbName/storeName or delete the old database from DevTools (Application -> IndexedDB).`);
                        }
                        if (!store.indexNames.contains(INDEX_BY_NS)) {
                            store.createIndex(INDEX_BY_NS, "ns", { unique: false });
                        }
                        if (!store.indexNames.contains(INDEX_BY_EXPIRY)) {
                            store.createIndex(INDEX_BY_EXPIRY, "e", { unique: false });
                        }
                    };
                    req.onblocked = () => {
                        // Another tab may still hold the DB open during upgrade.
                        // Not fatal; but operations can hang until the other tab releases.
                        // You can hook this if you want to show UI to the user.
                        // console.warn(`[idb-kv] Open blocked for ${dbName}`);
                    };
                    req.onerror = () => reject(req.error);
                    req.onsuccess = () => {
                        const db = req.result;
                        // If another tab upgrades the DB, this connection becomes stale.
                        db.onversionchange = () => {
                            try {
                                db.close();
                            }
                            finally {
                                dbPromiseCache.delete(key);
                            }
                        };
                        resolve(db);
                    };
                });
                dbPromiseCache.set(key, p);
                p.catch(() => dbPromiseCache.delete(key));
                return p;
            });
        }
        function runTx(dbName, storeName, mode, fn) {
            return __awaiter(this, void 0, void 0, function* () {
                const db = yield openDatabase(dbName, storeName);
                const tx = db.transaction(storeName, mode);
                const store = tx.objectStore(storeName);
                try {
                    const result = yield fn(store, tx);
                    yield txDone(tx);
                    return result;
                }
                catch (err) {
                    try {
                        tx.abort();
                    }
                    catch (_a) {
                        // ignore
                    }
                    throw err;
                }
            });
        }
        function closeCachedDB(dbName, storeName) {
            return __awaiter(this, void 0, void 0, function* () {
                const key = cacheKey(dbName, storeName);
                const p = dbPromiseCache.get(key);
                if (!p)
                    return;
                dbPromiseCache.delete(key);
                try {
                    const db = yield p;
                    db.close();
                }
                catch (_a) {
                    // ignore
                }
            });
        }
        function createKV(options = {}) {
            var _a, _b, _c;
            const dbName = (_a = options.dbName) !== null && _a !== void 0 ? _a : DEFAULT_DB;
            const storeName = (_b = options.storeName) !== null && _b !== void 0 ? _b : DEFAULT_STORE;
            const namespace = (_c = options.namespace) !== null && _c !== void 0 ? _c : "default";
            const withNamespace = (ns) => createKV({ dbName, storeName, namespace: ns });
            const makePrimaryKey = (key) => [namespace, key];
            function getRecord(key) {
                return __awaiter(this, void 0, void 0, function* () {
                    return runTx(dbName, storeName, "readonly", (store) => __awaiter(this, void 0, void 0, function* () {
                        const req = store.get(makePrimaryKey(key));
                        const rec = (yield promisifyRequest(req));
                        return rec;
                    }));
                });
            }
            function deleteIfExpired(key, rec) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!rec || !isExpired(rec.e))
                        return false;
                    // Best effort: remove expired record.
                    yield runTx(dbName, storeName, "readwrite", (store) => {
                        store.delete(makePrimaryKey(key));
                    });
                    return true;
                });
            }
            return {
                withNamespace,
                get(key) {
                    return __awaiter(this, void 0, void 0, function* () {
                        const rec = yield getRecord(key);
                        if (!rec)
                            return undefined;
                        if (isExpired(rec.e)) {
                            yield deleteIfExpired(key, rec);
                            return undefined;
                        }
                        return rec.v;
                    });
                },
                getMeta(key) {
                    return __awaiter(this, void 0, void 0, function* () {
                        const rec = yield getRecord(key);
                        if (!rec)
                            return undefined;
                        if (isExpired(rec.e)) {
                            yield deleteIfExpired(key, rec);
                            return undefined;
                        }
                        return { value: rec.v, expiresAt: rec.e };
                    });
                },
                set(key, value, opts) {
                    return __awaiter(this, void 0, void 0, function* () {
                        const expiresAt = normalizeExpiresAt(opts);
                        yield runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            // If it's already expired, treat as delete (avoids accumulating dead records).
                            if (typeof expiresAt === "number" && expiresAt <= Date.now()) {
                                store.delete(makePrimaryKey(key));
                                return;
                            }
                            const rec = typeof expiresAt === "number"
                                ? { ns: namespace, k: key, v: value, e: expiresAt }
                                : { ns: namespace, k: key, v: value };
                            store.put(rec);
                        }));
                    });
                },
                del(key) {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield runTx(dbName, storeName, "readwrite", (store) => {
                            store.delete(makePrimaryKey(key));
                        });
                    });
                },
                has(key) {
                    return __awaiter(this, void 0, void 0, function* () {
                        const rec = yield getRecord(key);
                        if (!rec)
                            return false;
                        if (isExpired(rec.e)) {
                            yield deleteIfExpired(key, rec);
                            return false;
                        }
                        return true;
                    });
                },
                keys() {
                    return __awaiter(this, void 0, void 0, function* () {
                        return runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const idx = store.index(INDEX_BY_NS);
                            const range = IDBKeyRange.only(namespace);
                            const keys = [];
                            // Need values to check expiry; so we use openCursor on the index.
                            const req = idx.openCursor(range);
                            yield new Promise((resolve, reject) => {
                                req.onerror = () => reject(req.error);
                                req.onsuccess = () => {
                                    const cursor = req.result;
                                    if (!cursor) {
                                        resolve();
                                        return;
                                    }
                                    const rec = cursor.value;
                                    if (isExpired(rec.e)) {
                                        cursor.delete(); // lazy cleanup
                                    }
                                    else {
                                        keys.push(rec.k);
                                    }
                                    cursor.continue();
                                };
                            });
                            return keys;
                        }));
                    });
                },
                values() {
                    return __awaiter(this, void 0, void 0, function* () {
                        return runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const idx = store.index(INDEX_BY_NS);
                            const range = IDBKeyRange.only(namespace);
                            const values = [];
                            const req = idx.openCursor(range);
                            yield new Promise((resolve, reject) => {
                                req.onerror = () => reject(req.error);
                                req.onsuccess = () => {
                                    const cursor = req.result;
                                    if (!cursor) {
                                        resolve();
                                        return;
                                    }
                                    const rec = cursor.value;
                                    if (isExpired(rec.e)) {
                                        cursor.delete();
                                    }
                                    else {
                                        values.push(rec.v);
                                    }
                                    cursor.continue();
                                };
                            });
                            return values;
                        }));
                    });
                },
                entries() {
                    return __awaiter(this, void 0, void 0, function* () {
                        return runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const idx = store.index(INDEX_BY_NS);
                            const range = IDBKeyRange.only(namespace);
                            const entries = [];
                            const req = idx.openCursor(range);
                            yield new Promise((resolve, reject) => {
                                req.onerror = () => reject(req.error);
                                req.onsuccess = () => {
                                    const cursor = req.result;
                                    if (!cursor) {
                                        resolve();
                                        return;
                                    }
                                    const rec = cursor.value;
                                    if (isExpired(rec.e)) {
                                        cursor.delete();
                                    }
                                    else {
                                        entries.push([rec.k, rec.v]);
                                    }
                                    cursor.continue();
                                };
                            });
                            return entries;
                        }));
                    });
                },
                clear() {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const idx = store.index(INDEX_BY_NS);
                            const range = IDBKeyRange.only(namespace);
                            const req = idx.openCursor(range);
                            yield new Promise((resolve, reject) => {
                                req.onerror = () => reject(req.error);
                                req.onsuccess = () => {
                                    const cursor = req.result;
                                    if (!cursor) {
                                        resolve();
                                        return;
                                    }
                                    cursor.delete();
                                    cursor.continue();
                                };
                            });
                        }));
                    });
                },
                getMany(keys) {
                    return __awaiter(this, void 0, void 0, function* () {
                        // Single readwrite tx so we can lazily delete expired items.
                        return runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const now = Date.now();
                            // Schedule all gets synchronously
                            const reqs = keys.map((k) => promisifyRequest(store.get([namespace, k])));
                            const recs = (yield Promise.all(reqs));
                            const out = new Array(keys.length);
                            // After reads, delete expired (still inside same task; safe in modern browsers)
                            for (let i = 0; i < keys.length; i++) {
                                const rec = recs[i];
                                if (!rec) {
                                    out[i] = undefined;
                                    continue;
                                }
                                if (typeof rec.e === "number" && rec.e <= now) {
                                    store.delete([namespace, keys[i]]);
                                    out[i] = undefined;
                                }
                                else {
                                    out[i] = rec.v;
                                }
                            }
                            return out;
                        }));
                    });
                },
                setMany(entries, opts) {
                    return __awaiter(this, void 0, void 0, function* () {
                        const expiresAt = normalizeExpiresAt(opts);
                        yield runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const now = Date.now();
                            for (const [key, value] of entries) {
                                if (typeof expiresAt === "number" && expiresAt <= now) {
                                    store.delete([namespace, key]);
                                    continue;
                                }
                                const rec = typeof expiresAt === "number"
                                    ? { ns: namespace, k: key, v: value, e: expiresAt }
                                    : { ns: namespace, k: key, v: value };
                                store.put(rec);
                            }
                        }));
                    });
                },
                delMany(keys) {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            for (const key of keys) {
                                store.delete([namespace, key]);
                            }
                        }));
                    });
                },
                cleanupExpired() {
                    return __awaiter(this, arguments, void 0, function* (scope = "namespace") {
                        return runTx(dbName, storeName, "readwrite", (store) => __awaiter(this, void 0, void 0, function* () {
                            const now = Date.now();
                            let deleted = 0;
                            if (scope === "all") {
                                // Efficient across all namespaces: only records that have e are in this index.
                                const idx = store.index(INDEX_BY_EXPIRY);
                                const range = IDBKeyRange.upperBound(now);
                                const req = idx.openCursor(range);
                                yield new Promise((resolve, reject) => {
                                    req.onerror = () => reject(req.error);
                                    req.onsuccess = () => {
                                        const cursor = req.result;
                                        if (!cursor) {
                                            resolve();
                                            return;
                                        }
                                        cursor.delete();
                                        deleted++;
                                        cursor.continue();
                                    };
                                });
                                return deleted;
                            }
                            // Namespace-only cleanup: scan by namespace and delete expired.
                            const idx = store.index(INDEX_BY_NS);
                            const range = IDBKeyRange.only(namespace);
                            const req = idx.openCursor(range);
                            yield new Promise((resolve, reject) => {
                                req.onerror = () => reject(req.error);
                                req.onsuccess = () => {
                                    const cursor = req.result;
                                    if (!cursor) {
                                        resolve();
                                        return;
                                    }
                                    const rec = cursor.value;
                                    if (typeof rec.e === "number" && rec.e <= now) {
                                        cursor.delete();
                                        deleted++;
                                    }
                                    cursor.continue();
                                };
                            });
                            return deleted;
                        }));
                    });
                },
                close() {
                    return __awaiter(this, void 0, void 0, function* () {
                        yield closeCachedDB(dbName, storeName);
                    });
                },
            };
        }
        IDB.createKV = createKV;
    })(IDB = CS.IDB || (CS.IDB = {}));
})(CS || (CS = {}));

//# sourceMappingURL=cs-idb-kv.js.map
